import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * Cliente da Calibragem (#877), com aliases finos sobre os tipos gerados.
 *
 * Como no `diagnostic.ts`: escrever o contrato à mão criaria um segundo
 * contrato que diverge em silêncio. A fonte de verdade continua sendo
 * `api.generated.ts`.
 */
export type Calibration = components["schemas"]["CalibrationOut"];
export type CalibrationScope = components["schemas"]["CalibrationScopeOut"];
export type CalibrationScopeItem = components["schemas"]["ScopeItemOut"];
export type CalibrationDecisionItem = components["schemas"]["DecisionItemOut"];
export type CalibrationPreview = components["schemas"]["PreviewOut"];
export type CalibrationStep = components["schemas"]["StepOut"];
export type PreviewRequest = components["schemas"]["PreviewIn"];
export type PublishRequest = components["schemas"]["PublishIn"];
export type CalibrationParameter = components["schemas"]["ParameterOut"];

/**
 * A lista da Calibragem: os itens Ativar da aprovação vigente com esforço no
 * catálogo, e à parte os em decisão e os Ativar sem esforço.
 */
export async function getScopeItems(projectId: string): Promise<CalibrationScope> {
  const { data } = await apiClient.get<CalibrationScope>(
    `/api/projects/${projectId}/calibration/scope-items`,
  );
  return data;
}

/** Os 55 parâmetros da versão ativa, com família, rótulo e a marca de premissa. */
export async function getParameters(projectId: string): Promise<CalibrationParameter[]> {
  const { data } = await apiClient.get<CalibrationParameter[]>(
    `/api/projects/${projectId}/calibration/parameters`,
  );
  return data;
}

/** A calibragem ativa. 404 quando o projeto ainda não tem nenhuma. */
export async function getCalibration(projectId: string): Promise<Calibration> {
  const { data } = await apiClient.get<Calibration>(
    `/api/projects/${projectId}/calibration`,
  );
  return data;
}

/** Abre uma calibragem, congelando o baseline dos dois agentes. */
export async function openCalibration(projectId: string): Promise<Calibration> {
  const { data } = await apiClient.post<Calibration>(
    `/api/projects/${projectId}/calibration`,
  );
  return data;
}

/**
 * Recalcula sem gravar.
 *
 * É o que sustenta "sair sem publicar não altera o que o Diagnóstico mostra":
 * esta chamada não escreve nada, e quem publica é `publishCalibration`.
 */
export async function previewCalibration(
  projectId: string,
  body: PreviewRequest,
): Promise<CalibrationPreview> {
  const { data } = await apiClient.post<CalibrationPreview>(
    `/api/projects/${projectId}/calibration/preview`,
    body,
  );
  return data;
}

/**
 * Congela o que a pessoa ajustou e enfileira a publicação.
 *
 * A seleção e os números o servidor monta: são os itens Ativar da aprovação
 * vigente, calculados pelo motor com estes ajustes.
 */
export async function publishCalibration(
  projectId: string,
  body: PublishRequest,
): Promise<Calibration> {
  const { data } = await apiClient.post<Calibration>(
    `/api/projects/${projectId}/calibration/publish`,
    body,
  );
  return data;
}
